import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { normalizeAdminSummary } from '../../../api/chats';
import { Button } from '../../../components/ui';
import type { ChatAdminSummary } from '../../../types/admin';
import { useAdminRoster } from '../../chats/hooks/useAdminRoster';
import {
  usePatientAssignments,
  useUpdatePatientAssignments,
} from '../hooks/usePatientAssignments';
import styles from './AssignedAdminsPanel.module.scss';

type AssignedAdminsPanelProps = {
  readonly patientId: string;
};

export function AssignedAdminsPanel({ patientId }: AssignedAdminsPanelProps) {
  const { t } = useTranslation();
  const { data: assignments, isLoading } = usePatientAssignments(patientId);
  const { data: rosterData, isLoading: rosterLoading, error: rosterError } = useAdminRoster();
  const updateAssignments = useUpdatePatientAssignments(patientId);
  const [isEditing, setIsEditing] = useState(false);
  const [selected, setSelected] = useState<readonly string[]>([]);

  const assignedAdminIds = useMemo(
    () => (assignments ?? []).map((assignment) => assignment.admin_id),
    [assignments],
  );
  const hasSelectionChanged = useMemo(() => {
    if (selected.length !== assignedAdminIds.length) return true;
    const assignedSet = new Set(assignedAdminIds);
    return selected.some((id) => !assignedSet.has(id));
  }, [assignedAdminIds, selected]);

  useEffect(() => {
    setSelected(assignedAdminIds);
  }, [assignedAdminIds]);

  const roster = useMemo(() => {
    const baseRoster = rosterData ?? [];
    const knownIds = new Set(baseRoster.map((admin) => admin.admin_id));
    const fromAssignments: ChatAdminSummary[] = (assignments ?? [])
      .filter((assignment) => !knownIds.has(assignment.admin_id))
      .map((assignment) =>
        normalizeAdminSummary({
          admin_id: assignment.admin_id,
          user_id: assignment.user_id,
          display_name: assignment.display_name,
        }),
      );
    return [...baseRoster, ...fromAssignments];
  }, [rosterData, assignments]);

  async function handleSave() {
    if (!hasSelectionChanged) return;
    try {
      await updateAssignments.mutateAsync(selected);
      setIsEditing(false);
    } catch {
      // Error surfaced via updateAssignments.error.
    }
  }

  function handleCancel() {
    setSelected(assignedAdminIds);
    setIsEditing(false);
    updateAssignments.reset();
  }

  const assignedList = assignments ?? [];
  const errorMessage =
    updateAssignments.error instanceof Error
      ? updateAssignments.error.message
      : updateAssignments.isError
        ? t('clientDetail.assignedAdminsSaveError')
        : null;
  const rosterErrorMessage =
    rosterError instanceof Error
      ? rosterError.message
      : rosterError
        ? t('clientDetail.assignedAdminsRosterError')
        : null;

  return (
    <div
      className={styles.panel}
      aria-busy={isLoading || rosterLoading || updateAssignments.isPending}
    >
      {isLoading ? (
        <div className={styles.loadingState} role="status">
          <span className={styles.loadingAvatar} />
          <span className={styles.loadingLine} />
        </div>
      ) : isEditing ? (
        <div className={styles.editor}>
          <div className={styles.editorHeader}>
            <div>
              <p className={styles.editorTitle}>{t('clientDetail.sectionAssignedAdmins')}</p>
              <p className={styles.selectionCount} aria-live="polite">
                {t('chats.assignedAdminsCount', { count: selected.length })}
              </p>
            </div>
          </div>

          {rosterLoading ? (
            <p className={styles.rosterStatus} role="status">
              {t('clientDetail.assignedAdminsRosterLoading')}
            </p>
          ) : rosterErrorMessage ? (
            <p className={styles.error} role="alert">{rosterErrorMessage}</p>
          ) : roster.length === 0 ? (
            <p className={styles.rosterStatus}>{t('clientDetail.assignedAdminsNone')}</p>
          ) : (
            <fieldset className={styles.rosterFieldset}>
              <legend className={styles.visuallyHidden}>
                {t('clientDetail.sectionAssignedAdmins')}
              </legend>
              <div className={styles.rosterGrid}>
                {roster.map((admin) => {
                  const checked = selected.includes(admin.admin_id);
                  return (
                    <label
                      key={admin.admin_id}
                      className={`${styles.rosterOption} ${checked ? styles.rosterOptionChecked : ''}`}
                    >
                      <span
                        className={styles.avatar}
                        style={{ backgroundColor: admin.accent_color }}
                      >
                        {admin.initials}
                      </span>
                      <span className={styles.rosterIdentity}>
                        <span className={styles.rosterName}>{admin.full_name}</span>
                        <span className={styles.availability}>
                          <span
                            className={`${styles.statusDot} ${admin.is_online ? styles.statusDotOnline : ''}`}
                            aria-hidden="true"
                          />
                          {admin.is_online ? t('chats.available') : t('chats.offline')}
                        </span>
                      </span>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() =>
                          setSelected((current) =>
                            checked
                              ? current.filter((id) => id !== admin.admin_id)
                              : [...current, admin.admin_id],
                          )
                        }
                      />
                    </label>
                  );
                })}
              </div>
            </fieldset>
          )}

          {errorMessage && <p className={styles.error} role="alert">{errorMessage}</p>}
          <div className={styles.editorActions}>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              disabled={updateAssignments.isPending}
            >
              {t('clientDetail.assignedAdminsCancel')}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => void handleSave()}
              disabled={updateAssignments.isPending || rosterLoading || !hasSelectionChanged}
            >
              {updateAssignments.isPending
                ? t('clientDetail.assignedAdminsSaving')
                : t('clientDetail.assignedAdminsSave')}
            </Button>
          </div>
        </div>
      ) : (
        <div className={styles.summary}>
          <div className={styles.summaryContent}>
            <span className={styles.countBadge} aria-hidden="true">{assignedList.length}</span>
            {assignedList.length > 0 ? (
              <div className={styles.adminList}>
                {assignedList.map((admin) => {
                  const parts = admin.display_name.trim().split(/\s+/);
                  const initials =
                    parts
                      .slice(0, 2)
                      .map((part) => part[0]?.toUpperCase() ?? '')
                      .join('') || 'WF';
                  return (
                    <span key={admin.admin_id} className={styles.adminChip}>
                      <span className={styles.avatar}>{initials}</span>
                      <span className={styles.adminChipName}>{admin.display_name}</span>
                    </span>
                  );
                })}
              </div>
            ) : (
              <p className={styles.emptyState}>{t('clientDetail.assignedAdminsNone')}</p>
            )}
          </div>
          <Button variant="secondary" size="sm" onClick={() => setIsEditing(true)}>
            {t('clientDetail.assignedAdminsManage')}
          </Button>
        </div>
      )}
    </div>
  );
}
